import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Search,
  Plus,
  Pencil,
  Trash2,
  Cpu,
  Users,
  CreditCard,
  Calendar,
  RefreshCw,
  Bell,
  X,
  FileText,
  LogIn,
  Key,
  Shuffle,
  Copy,
  Check,
  Shield,
  Loader2,
  ChevronDown,
  Eye,
  Receipt,
  Info,
  Download,
  Layers,
  Zap,
  Ban,
  History as HistoryIcon,
} from 'lucide-react';
import { adminApi } from '../../services/api';
import { useTenantContextStore } from '../../store/tenant-context.store';

interface TenantInvoice {
  id: string;
  invoiceNumber: string | null;
  amountDue: number;
  amountPaid: number;
  status: string;
  description?: string | null;
  createdAt: string;
  dueDate: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  paidAt?: string | null;
}

interface Subscription {
  id: string;
  name: string | null;
  planId: string;
  plan?: {
    id: string;
    name: string;
    displayName: string;
    priceYearly?: number;
    priceOneShot?: number;
    stripePriceIdYearly?: string | null;
    stripePriceIdOneShot?: string | null;
    stripeTaxRateId?: string | null;
    usersPerTracker?: number;
    geofencesPerTracker?: number;
    retentionDays?: number;
    features?: any;
  };
  quantity: number;
  pricingType: string;
  status: string;
  startDate: string | null;
  currentPeriodStart?: string | null;
  autoRenewal: boolean;
  alertDays: number[];
  alertsEnabled: boolean;
  currentPeriodEnd: string | null;
  expiresAt: string | null;
  canceledAt?: string | null;
  notes: string | null;
  deviceIds?: string[];
  invoices?: TenantInvoice[];
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  isActive: boolean;
  maxDevices: number;
  maxUsers: number;
  maxGeofences: number;
  retentionDays: number;
  createdAt: string;
  // Company info
  companyName?: string | null;
  address?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  postalCode?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  vatNumber?: string | null;
  siret?: string | null;
  website?: string | null;
  // Billing
  billingEmail?: string | null;
  billingAddress?: string | null;
  vatRate?: number;
  // Duo Security
  duoClientId?: string | null;
  duoClientSecret?: string | null;
  duoApiHostname?: string | null;
  duoEnabled?: boolean;
  // API Keys
  apiKeysEnabled?: boolean;
  // Subscriptions
  subscriptions?: Subscription[];
  subscription?: { id: string; status: string; expiresAt?: string | null; currentPeriodEnd?: string | null } | null;
  _count?: {
    devices: number;
    users: number;
  };
}

interface Plan {
  id: string;
  name: string;
  displayName: string;
  priceYearly: number;
  priceOneShot: number;
}

interface TenantDevice {
  id: string;
  name: string;
  plate: string | null;
  imei: string;
}

type TabId = 'general' | 'company' | 'billing' | 'subscriptions' | 'modules' | 'security';

interface TenantModuleRow {
  definition: {
    key: string;
    label: string;
    description: string;
    defaultPriceCents: number;
    icon: string;
    priority: 1 | 2 | 3;
    dependencies?: string[];
  };
  state: {
    enabled: boolean;
    pricePerVehicleCents: number;
    seatsSold: number | null;
    pricePerSeatCents: number | null;
    missionSeatsUsed: number | null;
    trialEndsAt: string | null;
    activatedAt: string | null;
    deactivatedAt: string | null;
    stripeSubscriptionItemId: string | null;
    stripePriceId: string | null;
    createdAt: string | null;
    updatedAt: string | null;
  };
}

interface ModuleEventRow {
  id: string;
  moduleKey: string;
  action: string;
  previousState: unknown;
  newState: unknown;
  reason: string | null;
  triggeredBy: { id: string; firstName: string; lastName: string; email: string } | null;
  createdAt: string;
}

export default function TenantsPage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const tenantsLimit = 20;
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordTenant, setPasswordTenant] = useState<Tenant | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordCopied, setPasswordCopied] = useState(false);

  // Subscription detail & invoice modals
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [viewingInvoice, setViewingInvoice] = useState<TenantInvoice | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [payingSubId, setPayingSubId] = useState<string | null>(null);
  const [markingPaidId, setMarkingPaidId] = useState<string | null>(null);

  // Tenant add-on modules tab state
  const [tenantModules, setTenantModules] = useState<TenantModuleRow[]>([]);
  const [tenantModulesLoading, setTenantModulesLoading] = useState(false);
  const [tenantActiveVehicles, setTenantActiveVehicles] = useState(0);
  const [tenantEvVehicles, setTenantEvVehicles] = useState(0);
  const [moduleBusyKey, setModuleBusyKey] = useState<string | null>(null);
  const [moduleEventsOpen, setModuleEventsOpen] = useState(false);
  const [moduleEvents, setModuleEvents] = useState<ModuleEventRow[]>([]);
  const [moduleEventsFilter, setModuleEventsFilter] = useState<string>('');

  // Generate random password
  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
    setPasswordError('');
    setPasswordCopied(false);
  };

  // Copy password to clipboard
  const copyPassword = async () => {
    if (newPassword) {
      await navigator.clipboard.writeText(newPassword);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    }
  };
  const { setSelectedTenant } = useTenantContextStore();
  const navigate = useNavigate();

  // Form data for tenant
  const [formData, setFormData] = useState({
    // General
    name: '',
    slug: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    plan: 'FREE',
    maxDevices: 3,
    maxUsers: 1,
    maxGeofences: 5,
    retentionDays: 7,
    isActive: true,
    // Company info
    companyName: '',
    address: '',
    addressLine2: '',
    city: '',
    postalCode: '',
    country: 'FR',
    phone: '',
    companyEmail: '',
    vatNumber: '',
    siret: '',
    website: '',
    // Billing
    billingEmail: '',
    billingAddress: '',
    vatRate: 17,
    // Duo Security
    duoClientId: '',
    duoClientSecret: '',
    duoApiHostname: '',
    duoEnabled: false,
    apiKeysEnabled: false,
  });

  const [duoHealthCheckResult, setDuoHealthCheckResult] = useState<{ success: boolean; message: string } | null>(null);
  const [duoHealthCheckLoading, setDuoHealthCheckLoading] = useState(false);

  // Subscription form
  const [subscriptionForm, setSubscriptionForm] = useState({
    name: '',
    planId: '',
    quantity: 1,
    pricingType: 'YEARLY',
    status: 'ACTIVE',
    startDate: '',
    autoRenewal: true,
    alertDays: [30, 15, 7],
    alertsEnabled: true,
    expiresAt: '',
    notes: '',
    deviceIds: [] as string[],
  });

  // Devices du tenant pour le multi-select
  const [tenantDevices, setTenantDevices] = useState<TenantDevice[]>([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);
  const [deviceSearch, setDeviceSearch] = useState('');
  const subDeviceDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadData();
  }, [search, planFilter, statusFilter, page, sortBy, sortOrder]);

  useEffect(() => {
    loadPlans();
  }, []);

  // Load tenant modules when Modules OR Subscriptions tab is opened — the
  // Subscriptions tab now shows a billing summary that includes add-on modules.
  useEffect(() => {
    if ((activeTab === 'modules' || activeTab === 'subscriptions') && editingTenant) {
      loadTenantModules(editingTenant.id);
    }
  }, [activeTab, editingTenant]);

  // Close device dropdown on outside click
  useEffect(() => {
    if (!showDeviceDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (subDeviceDropdownRef.current && !subDeviceDropdownRef.current.contains(e.target as Node)) {
        setShowDeviceDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDeviceDropdown]);

  const loadData = async () => {
    try {
      const tenantsRes = await adminApi.getTenants({
        search: search || undefined,
        plan: planFilter || undefined,
        status: statusFilter || undefined,
        page,
        limit: tenantsLimit,
        sortBy,
        sortOrder,
      });
      const data = tenantsRes.data?.tenants || tenantsRes.data?.data || tenantsRes.data;
      setTenants(Array.isArray(data) ? data : []);
      setTotal(tenantsRes.data?.pagination?.total || (Array.isArray(data) ? data.length : 0));
    } catch (error) {
      console.error('Erreur chargement tenants:', error);
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPlans = async () => {
    try {
      const plansRes = await adminApi.getPlans();
      setPlans(Array.isArray(plansRes.data) ? plansRes.data : []);
    } catch (error) {
      console.error('Erreur chargement plans:', error);
    }
  };

  const loadTenantDetails = async (tenantId: string) => {
    try {
      const res = await adminApi.getTenant(tenantId);
      return res.data;
    } catch (error) {
      console.error('Erreur chargement tenant:', error);
      return null;
    }
  };

  const loadTenantDevices = async (tenantId: string) => {
    try {
      const res = await adminApi.getDevices({ tenantId, limit: 200 });
      const devices = res.data?.data || res.data?.devices || res.data;
      setTenantDevices(Array.isArray(devices) ? devices : []);
    } catch (error) {
      console.error('Erreur chargement devices:', error);
      setTenantDevices([]);
    }
  };

  const loadTenantModules = async (tenantId: string) => {
    setTenantModulesLoading(true);
    try {
      const res = await adminApi.getTenantModules(tenantId);
      setTenantModules(res.data.modules);
      setTenantActiveVehicles(res.data.activeVehicles);
      setTenantEvVehicles(res.data.evVehicles ?? 0);
    } catch (error) {
      console.error('Erreur chargement modules:', error);
      setTenantModules([]);
      setTenantActiveVehicles(0);
      setTenantEvVehicles(0);
    } finally {
      setTenantModulesLoading(false);
    }
  };

  const loadModuleEvents = async (tenantId: string, moduleKey?: string) => {
    try {
      const res = await adminApi.getTenantModuleEvents(tenantId, {
        moduleKey: moduleKey || undefined,
        limit: 100,
      });
      setModuleEvents(res.data);
    } catch (error) {
      console.error('Erreur chargement events:', error);
      setModuleEvents([]);
    }
  };

  const handleActivateModule = async (moduleKey: string, trial: boolean, priceCents: number) => {
    if (!editingTenant) return;
    setModuleBusyKey(moduleKey);
    try {
      await adminApi.activateTenantModule(editingTenant.id, moduleKey, { trial, priceCents });
      await loadTenantModules(editingTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erreur activation');
    } finally {
      setModuleBusyKey(null);
    }
  };

  const handleDeactivateModule = async (moduleKey: string) => {
    if (!editingTenant) return;
    const msg =
      moduleKey === 'FIELD_SERVICE'
        ? `Désactiver le module Missions Terrain ? Les sièges payés pour l'année en cours sont perdus (pas de remboursement, pas de proration). Le siège Stripe sera retiré au prochain cycle.`
        : `Désactiver le module ${moduleKey} pour ce tenant ? La facturation Stripe sera retirée au prorata.`;
    if (!confirm(msg)) return;
    setModuleBusyKey(moduleKey);
    try {
      await adminApi.deactivateTenantModule(editingTenant.id, moduleKey);
      await loadTenantModules(editingTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erreur désactivation');
    } finally {
      setModuleBusyKey(null);
    }
  };

  const handleUpdateModulePrice = async (moduleKey: string, priceCents: number) => {
    if (!editingTenant) return;
    setModuleBusyKey(moduleKey);
    try {
      await adminApi.updateTenantModulePrice(editingTenant.id, moduleKey, priceCents);
      await loadTenantModules(editingTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erreur mise à jour du prix');
    } finally {
      setModuleBusyKey(null);
    }
  };

  const handleUpdateModuleSeats = async (
    moduleKey: string,
    seatsSold: number,
    pricePerSeatCents: number,
  ) => {
    if (!editingTenant) return;
    setModuleBusyKey(moduleKey);
    try {
      await adminApi.updateTenantModuleSeats(
        editingTenant.id,
        moduleKey,
        seatsSold,
        pricePerSeatCents,
      );
      await loadTenantModules(editingTenant.id);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erreur mise à jour des sièges');
    } finally {
      setModuleBusyKey(null);
    }
  };

  const filteredDevices = tenantDevices.filter((d) => {
    if (!deviceSearch) return true;
    const q = deviceSearch.toLowerCase();
    return (
      d.name.toLowerCase().includes(q) ||
      (d.plate && d.plate.toLowerCase().includes(q)) ||
      d.imei.toLowerCase().includes(q)
    );
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await adminApi.createTenant({
        name: formData.name,
        slug: formData.slug,
        adminEmail: formData.email,
        adminPassword: formData.password,
        adminFirstName: formData.firstName,
        adminLastName: formData.lastName,
        plan: formData.plan,
      });
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Erreur creation tenant:', error);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTenant) return;
    try {
      await adminApi.updateTenant(editingTenant.id, {
        name: formData.name,
        plan: formData.plan,
        maxDevices: formData.maxDevices,
        maxUsers: formData.maxUsers,
        maxGeofences: formData.maxGeofences,
        retentionDays: formData.retentionDays,
        isActive: formData.isActive,
        // Company info
        companyName: formData.companyName || null,
        address: formData.address || null,
        addressLine2: formData.addressLine2 || null,
        city: formData.city || null,
        postalCode: formData.postalCode || null,
        country: formData.country || null,
        phone: formData.phone || null,
        email: formData.companyEmail || null,
        vatNumber: formData.vatNumber || null,
        siret: formData.siret || null,
        website: formData.website || null,
        // Billing
        billingEmail: formData.billingEmail || null,
        billingAddress: formData.billingAddress || null,
        vatRate: formData.vatRate,
        // Duo Security
        duoClientId: formData.duoClientId || null,
        duoClientSecret: formData.duoClientSecret || null,
        duoApiHostname: formData.duoApiHostname || null,
        duoEnabled: formData.duoEnabled,
        // API Keys
        apiKeysEnabled: formData.apiKeysEnabled,
      });
      setShowModal(false);
      setEditingTenant(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Erreur mise à jour tenant:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Supprimer ce tenant et toutes ses données ?')) return;
    try {
      await adminApi.deleteTenant(id);
      loadData();
    } catch (error) {
      console.error('Erreur suppression tenant:', error);
    }
  };

  const handleResetPassword = async () => {
    if (!passwordTenant) return;
    if (newPassword.length < 8) {
      setPasswordError('Le mot de passe doit contenir au moins 8 caracteres');
      return;
    }
    try {
      const res = await adminApi.resetTenantPassword(passwordTenant.id, newPassword);
      alert(`Mot de passe reinitialise pour ${res.data.email}`);
      setShowPasswordModal(false);
      setPasswordTenant(null);
      setNewPassword('');
      setPasswordError('');
    } catch (error) {
      console.error('Erreur reinitialisation mot de passe:', error);
      setPasswordError('Erreur lors de la reinitialisation');
    }
  };

  const handleToggleActive = async (tenant: Tenant) => {
    try {
      await adminApi.updateTenant(tenant.id, { isActive: !tenant.isActive });
      loadData();
    } catch (error) {
      console.error('Erreur toggle tenant:', error);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === tenants.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(tenants.map((t) => t.id)));
    }
  };

  const handleBulkActivate = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      await adminApi.bulkToggleActiveTenants(Array.from(selectedIds), true);
      setSelectedIds(new Set());
      loadData();
    } catch (error) {
      console.error('Erreur activation en masse:', error);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDeactivate = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Désactiver ${selectedIds.size} tenant(s) ?`)) return;
    setBulkLoading(true);
    try {
      await adminApi.bulkToggleActiveTenants(Array.from(selectedIds), false);
      setSelectedIds(new Set());
      loadData();
    } catch (error) {
      console.error('Erreur désactivation en masse:', error);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Supprimer ${selectedIds.size} tenant(s) ? Cette action est irréversible.`)) return;
    setBulkLoading(true);
    try {
      await adminApi.bulkDeleteTenants(Array.from(selectedIds));
      setSelectedIds(new Set());
      loadData();
    } catch (error) {
      console.error('Erreur suppression en masse:', error);
    } finally {
      setBulkLoading(false);
    }
  };

  // Recalculer maxDevices à partir des abonnements actifs du tenant
  const recalcMaxDevices = (fullTenant: any) => {
    const totalSubDevices = (fullTenant.subscriptions || [])
      .filter((s: Subscription) => s.status === 'ACTIVE' || s.status === 'TRIALING')
      .reduce((sum: number, s: Subscription) => sum + (s.quantity || 0), 0);
    setFormData((prev) => ({
      ...prev,
      maxDevices: totalSubDevices > 0 ? totalSubDevices : fullTenant.maxDevices,
    }));
  };

  const openEditModal = async (tenant: Tenant) => {
    // Load full tenant details including subscriptions
    const fullTenant = await loadTenantDetails(tenant.id);
    if (!fullTenant) return;

    setEditingTenant(fullTenant);

    // Calculer le total des véhicules de tous les abonnements actifs
    const totalSubDevices = (fullTenant.subscriptions || [])
      .filter((s: Subscription) => s.status === 'ACTIVE' || s.status === 'TRIALING')
      .reduce((sum: number, s: Subscription) => sum + (s.quantity || 0), 0);

    setFormData({
      name: fullTenant.name,
      slug: fullTenant.slug,
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      plan: fullTenant.plan,
      maxDevices: totalSubDevices > 0 ? totalSubDevices : fullTenant.maxDevices,
      maxUsers: fullTenant.maxUsers,
      maxGeofences: fullTenant.maxGeofences,
      retentionDays: fullTenant.retentionDays,
      isActive: fullTenant.status === 'ACTIVE',
      // Company info
      companyName: fullTenant.companyName || '',
      address: fullTenant.address || '',
      addressLine2: fullTenant.addressLine2 || '',
      city: fullTenant.city || '',
      postalCode: fullTenant.postalCode || '',
      country: fullTenant.country || 'FR',
      phone: fullTenant.phone || '',
      companyEmail: fullTenant.email || '',
      vatNumber: fullTenant.vatNumber || '',
      siret: fullTenant.siret || '',
      website: fullTenant.website || '',
      // Billing
      billingEmail: fullTenant.billingEmail || '',
      billingAddress: fullTenant.billingAddress || '',
      vatRate: fullTenant.vatRate ?? 17,
      // Duo Security
      duoClientId: fullTenant.duoClientId || '',
      duoClientSecret: fullTenant.duoClientSecret || '',
      duoApiHostname: fullTenant.duoApiHostname || '',
      duoEnabled: fullTenant.duoEnabled || false,
      apiKeysEnabled: fullTenant.apiKeysEnabled || false,
    });
    setDuoHealthCheckResult(null);
    setActiveTab('general');
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      plan: 'FREE',
      maxDevices: 3,
      maxUsers: 1,
      maxGeofences: 5,
      retentionDays: 7,
      isActive: true,
      companyName: '',
      address: '',
      addressLine2: '',
      city: '',
      postalCode: '',
      country: 'FR',
      phone: '',
      companyEmail: '',
      vatNumber: '',
      siret: '',
      website: '',
      billingEmail: '',
      billingAddress: '',
      vatRate: 17,
      duoClientId: '',
      duoClientSecret: '',
      duoApiHostname: '',
      duoEnabled: false,
      apiKeysEnabled: false,
    });
    setDuoHealthCheckResult(null);
  };

  // Subscription handlers
  const openNewSubscriptionModal = () => {
    setEditingSubscription(null);
    setSubscriptionForm({
      name: '',
      planId: plans[0]?.id || '',
      quantity: 1,
      pricingType: 'YEARLY',
      status: 'ACTIVE',
      startDate: new Date().toISOString().split('T')[0],
      autoRenewal: true,
      alertDays: [30, 15, 7],
      alertsEnabled: true,
      expiresAt: '',
      notes: '',
      deviceIds: [],
    });
    setSelectedDeviceIds([]);
    setDeviceSearch('');
    setShowDeviceDropdown(false);
    if (editingTenant) loadTenantDevices(editingTenant.id);
    setShowSubscriptionModal(true);
  };

  const openEditSubscriptionModal = (sub: Subscription) => {
    setEditingSubscription(sub);
    const subDeviceIds = (sub as any).deviceIds || [];
    setSubscriptionForm({
      name: sub.name || '',
      planId: sub.planId,
      quantity: sub.quantity,
      pricingType: sub.pricingType,
      status: sub.status,
      startDate: sub.startDate ? sub.startDate.split('T')[0] : '',
      autoRenewal: sub.autoRenewal,
      alertDays: sub.alertDays || [30, 15, 7],
      alertsEnabled: sub.alertsEnabled,
      expiresAt: sub.expiresAt ? sub.expiresAt.split('T')[0] : '',
      notes: sub.notes || '',
      deviceIds: subDeviceIds,
    });
    setSelectedDeviceIds(subDeviceIds);
    setDeviceSearch('');
    setShowDeviceDropdown(false);
    if (editingTenant) loadTenantDevices(editingTenant.id);
    setShowSubscriptionModal(true);
  };

  const handleCreateSubscription = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTenant) return;
    try {
      await adminApi.createSubscription({
        tenantId: editingTenant.id,
        planId: subscriptionForm.planId,
        name: subscriptionForm.name || null,
        quantity: subscriptionForm.quantity,
        pricingType: subscriptionForm.pricingType,
        status: subscriptionForm.status,
        startDate: subscriptionForm.startDate ? new Date(subscriptionForm.startDate).toISOString() : null,
        autoRenewal: subscriptionForm.autoRenewal,
        alertDays: subscriptionForm.alertDays,
        alertsEnabled: subscriptionForm.alertsEnabled,
        expiresAt: subscriptionForm.expiresAt ? new Date(subscriptionForm.expiresAt).toISOString() : null,
        notes: subscriptionForm.notes || null,
        deviceIds: selectedDeviceIds,
      });
      setShowSubscriptionModal(false);
      // Reload tenant details et recalculer maxDevices
      const fullTenant = await loadTenantDetails(editingTenant.id);
      if (fullTenant) {
        setEditingTenant(fullTenant);
        recalcMaxDevices(fullTenant);
      }
    } catch (error) {
      console.error('Erreur creation abonnement:', error);
    }
  };

  const handleUpdateSubscription = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSubscription) return;
    try {
      await adminApi.updateSubscription(editingSubscription.id, {
        planId: subscriptionForm.planId,
        name: subscriptionForm.name || null,
        quantity: subscriptionForm.quantity,
        pricingType: subscriptionForm.pricingType,
        status: subscriptionForm.status,
        startDate: subscriptionForm.startDate ? new Date(subscriptionForm.startDate).toISOString() : null,
        autoRenewal: subscriptionForm.autoRenewal,
        alertDays: subscriptionForm.alertDays,
        alertsEnabled: subscriptionForm.alertsEnabled,
        expiresAt: subscriptionForm.expiresAt ? new Date(subscriptionForm.expiresAt).toISOString() : null,
        notes: subscriptionForm.notes || null,
        deviceIds: selectedDeviceIds,
      });
      setShowSubscriptionModal(false);
      if (editingTenant) {
        const fullTenant = await loadTenantDetails(editingTenant.id);
        if (fullTenant) {
          setEditingTenant(fullTenant);
          recalcMaxDevices(fullTenant);
        }
      }
    } catch (error) {
      console.error('Erreur mise a jour abonnement:', error);
    }
  };

  const handleRenewSubscription = async (subId: string) => {
    if (!confirm('Renouveler cet abonnement pour une nouvelle période ?')) return;
    try {
      await adminApi.renewSubscription(subId);
      if (editingTenant) {
        const fullTenant = await loadTenantDetails(editingTenant.id);
        if (fullTenant) {
          setEditingTenant(fullTenant);
          recalcMaxDevices(fullTenant);
        }
      }
    } catch (error) {
      console.error('Erreur renouvellement abonnement:', error);
    }
  };

  const handleDeleteSubscription = async (subId: string) => {
    if (!confirm('Supprimer cet abonnement ?')) return;
    try {
      await adminApi.deleteSubscription(subId, true);
      if (editingTenant) {
        const fullTenant = await loadTenantDetails(editingTenant.id);
        if (fullTenant) {
          setEditingTenant(fullTenant);
          recalcMaxDevices(fullTenant);
        }
      }
    } catch (error) {
      console.error('Erreur suppression abonnement:', error);
    }
  };

  // --- Subscription & Invoice utilities ---
  const isExpiringSoon = (sub: Subscription): boolean => {
    const endDate = sub.currentPeriodEnd || sub.expiresAt;
    if (!endDate) return false;
    const daysLeft = (new Date(endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    return daysLeft > 0 && daysLeft <= 30;
  };

  const handlePaySubscription = async (sub: Subscription) => {
    setPayingSubId(sub.id);
    try {
      const res = await adminApi.createSubscriptionCheckout(sub.id);
      if (res.data?.url) {
        window.open(res.data.url, '_blank');
      }
    } catch (error: any) {
      console.error('Erreur checkout Stripe:', error);
      alert(error.response?.data?.error || 'Erreur lors de la création du checkout');
    } finally {
      setPayingSubId(null);
    }
  };

  const handleViewInvoicePdf = async (invoice: TenantInvoice) => {
    setViewingInvoice(invoice);
    setLoadingPdf(true);
    try {
      const res = await adminApi.downloadInvoice(invoice.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (error) {
      console.error('Erreur chargement PDF:', error);
      alert('Erreur lors du chargement du PDF');
      setViewingInvoice(null);
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleMarkInvoicePaid = async (invoice: TenantInvoice) => {
    if (!confirm('Marquer cette facture comme payée ?')) return;
    setMarkingPaidId(invoice.id);
    try {
      await adminApi.markInvoicePaid(invoice.id);
      // Reload tenant details
      if (editingTenant) {
        const fullTenant = await loadTenantDetails(editingTenant.id);
        if (fullTenant) {
          setEditingTenant(fullTenant);
        }
      }
      // Update viewing invoice status
      if (viewingInvoice?.id === invoice.id) {
        setViewingInvoice({ ...invoice, status: 'PAID', paidAt: new Date().toISOString() });
      }
    } catch (error) {
      console.error('Erreur marquage payée:', error);
      alert('Erreur lors du marquage comme payée');
    } finally {
      setMarkingPaidId(null);
    }
  };

  const closePdfViewer = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setViewingInvoice(null);
  };

  const getInvoiceStatusBadge = (status: string) => {
    switch (status) {
      case 'PAID': return 'bg-green-100 text-green-700';
      case 'OPEN': case 'DRAFT': return 'bg-amber-100 text-amber-700';
      case 'VOID': case 'REFUSED': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatAmount = (amount: number) => {
    return amount.toFixed(2).replace('.', ',') + ' €';
  };

  const tabs: { id: TabId; label: string; icon: typeof Building2 }[] = [
    { id: 'general', label: 'Général', icon: Building2 },
    { id: 'company', label: 'Entreprise', icon: FileText },
    { id: 'billing', label: 'Facturation', icon: CreditCard },
    { id: 'subscriptions', label: 'Abonnements', icon: Calendar },
    { id: 'modules', label: 'Modules', icon: Layers },
    { id: 'security', label: 'Sécurité', icon: Shield },
  ];

  const handleDuoHealthCheck = async () => {
    if (!editingTenant) return;
    setDuoHealthCheckLoading(true);
    setDuoHealthCheckResult(null);
    try {
      // First save any pending Duo config changes
      await adminApi.updateTenant(editingTenant.id, {
        duoClientId: formData.duoClientId || null,
        duoClientSecret: formData.duoClientSecret || null,
        duoApiHostname: formData.duoApiHostname || null,
        duoEnabled: formData.duoEnabled,
      });
      const redirectUri = `${window.location.origin}/duo-callback`;
      const res = await adminApi.duoHealthCheck(editingTenant.id, redirectUri);
      setDuoHealthCheckResult(res.data);
    } catch (error: any) {
      setDuoHealthCheckResult({ success: false, message: error.response?.data?.message || 'Erreur de connexion' });
    } finally {
      setDuoHealthCheckLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Tenants</h1>
          <p className="text-gray-500">Gerer les organisations clientes</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingTenant(null);
            setActiveTab('general');
            setShowModal(true);
          }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          Nouveau Tenant
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 pl-10"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
          >
            <option value="">Tous les statuts</option>
            <option value="ACTIVE">Actifs</option>
            <option value="SUSPENDED">Inactifs</option>
          </select>
          <select
            value={planFilter}
            onChange={(e) => { setPlanFilter(e.target.value); setPage(1); }}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
          >
            <option value="">Tous les plans</option>
            <option value="FREE">Free</option>
            <option value="BASIC">Basic</option>
            <option value="PRO">Pro</option>
            <option value="ENTERPRISE">Enterprise</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3 flex items-center justify-between">
          <span className="text-sm font-medium text-cyan-800">
            {selectedIds.size} tenant{selectedIds.size > 1 ? 's' : ''} sélectionné{selectedIds.size > 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkActivate}
              disabled={bulkLoading}
              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {bulkLoading ? <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> : null}
              Activer
            </button>
            <button
              onClick={handleBulkDeactivate}
              disabled={bulkLoading}
              className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
            >
              Désactiver
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={bulkLoading}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              Supprimer
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="w-10 p-4">
                <input
                  type="checkbox"
                  checked={tenants.length > 0 && selectedIds.size === tenants.length}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
              </th>
              <th className="text-left p-4 text-gray-500 font-medium cursor-pointer select-none hover:text-gray-700" onClick={() => { if (sortBy === 'name') setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setSortBy('name'); setSortOrder('asc'); } setPage(1); }}>
                Tenant {sortBy === 'name' && (sortOrder === 'asc' ? '▲' : '▼')}
              </th>
              <th className="text-left p-4 text-gray-500 font-medium cursor-pointer select-none hover:text-gray-700" onClick={() => { if (sortBy === 'plan') setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setSortBy('plan'); setSortOrder('asc'); } setPage(1); }}>
                Plan {sortBy === 'plan' && (sortOrder === 'asc' ? '▲' : '▼')}
              </th>
              <th className="text-left p-4 text-gray-500 font-medium">Usage</th>
              <th className="text-left p-4 text-gray-500 font-medium cursor-pointer select-none hover:text-gray-700" onClick={() => { if (sortBy === 'status') setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setSortBy('status'); setSortOrder('asc'); } setPage(1); }}>
                Statut {sortBy === 'status' && (sortOrder === 'asc' ? '▲' : '▼')}
              </th>
              <th className="text-left p-4 text-gray-500 font-medium cursor-pointer select-none hover:text-gray-700" onClick={() => { if (sortBy === 'createdAt') setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setSortBy('createdAt'); setSortOrder('desc'); } setPage(1); }}>
                Créé le {sortBy === 'createdAt' && (sortOrder === 'asc' ? '▲' : '▼')}
              </th>
              <th className="text-left p-4 text-gray-500 font-medium">Expire le</th>
              <th className="text-right p-4 text-gray-500 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(tenants) && tenants.map((tenant) => (
              <tr key={tenant.id} className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${selectedIds.has(tenant.id) ? 'bg-cyan-50/50' : ''}`} onClick={() => openEditModal(tenant)}>
                <td className="w-10 p-4" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(tenant.id)}
                    onChange={() => toggleSelect(tenant.id)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-cyan-500/10">
                      <Building2 className="h-5 w-5 text-cyan-500" />
                    </div>
                    <div>
                      <p className="text-gray-900 font-medium">{tenant.name}</p>
                      <p className="text-gray-400 text-sm">{tenant.slug}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      tenant.plan === 'ENTERPRISE'
                        ? 'bg-purple-500/20 text-purple-400'
                        : tenant.plan === 'PRO'
                        ? 'bg-cyan-500/20 text-cyan-400'
                        : tenant.plan === 'BASIC'
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {tenant.plan}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-gray-500">
                      <Cpu className="h-4 w-4" />
                      <span>
                        {tenant._count?.devices || 0}/{tenant.maxDevices === -1 ? '∞' : tenant.maxDevices}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-500">
                      <Users className="h-4 w-4" />
                      <span>
                        {tenant._count?.users || 0}/{tenant.maxUsers === -1 ? '∞' : tenant.maxUsers}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => handleToggleActive(tenant)}
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      tenant.status === 'ACTIVE'
                        ? 'bg-green-500/20 text-green-600'
                        : 'bg-red-500/20 text-red-600'
                    }`}
                  >
                    {tenant.status === 'ACTIVE' ? 'Actif' : 'Inactif'}
                  </button>
                </td>
                <td className="p-4 text-gray-500">
                  {new Date(tenant.createdAt).toLocaleDateString('fr-FR')}
                </td>
                <td className="p-4 text-gray-500">
                  {(() => {
                    const exp = tenant.subscription?.expiresAt || tenant.subscription?.currentPeriodEnd;
                    if (!exp) return <span className="text-gray-300">—</span>;
                    const d = new Date(exp);
                    const now = new Date();
                    const days = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                    const cls = days < 0 ? 'text-red-600' : days <= 30 ? 'text-orange-500' : 'text-gray-500';
                    return <span className={cls}>{d.toLocaleDateString('fr-FR')}</span>;
                  })()}
                </td>
                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => {
                        setSelectedTenant({
                          id: tenant.id,
                          name: tenant.name,
                          slug: tenant.slug,
                        });
                        // Navigate to dashboard to view tenant data
                        navigate('/dashboard');
                      }}
                      className="p-2 hover:bg-cyan-50 rounded-lg transition-colors"
                      title="Administrer ce tenant"
                    >
                      <LogIn className="h-4 w-4 text-cyan-500" />
                    </button>
                    <button
                      onClick={() => {
                        setPasswordTenant(tenant);
                        setNewPassword('');
                        setPasswordError('');
                        setShowPasswordModal(true);
                      }}
                      className="p-2 hover:bg-amber-50 rounded-lg transition-colors"
                      title="Reinitialiser mot de passe admin"
                    >
                      <Key className="h-4 w-4 text-amber-500" />
                    </button>
                    <button
                      onClick={() => openEditModal(tenant)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Modifier"
                    >
                      <Pencil className="h-4 w-4 text-gray-500" />
                    </button>
                    <button
                      onClick={() => handleDelete(tenant.id)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {tenants.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Aucun tenant trouvé
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > tenantsLimit && (() => {
        const totalPages = Math.ceil(total / tenantsLimit);
        const getPageNumbers = () => {
          const pages: (number | '...')[] = [];
          if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
          } else {
            pages.push(1);
            if (page > 3) pages.push('...');
            const start = Math.max(2, page - 1);
            const end = Math.min(totalPages - 1, page + 1);
            for (let i = start; i <= end; i++) pages.push(i);
            if (page < totalPages - 2) pages.push('...');
            pages.push(totalPages);
          }
          return pages;
        };
        return (
          <div className="flex items-center justify-between">
            <p className="text-gray-500 text-sm">
              {(page - 1) * tenantsLimit + 1}-{Math.min(page * tenantsLimit, total)} sur {total} tenants
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Préc.
              </button>
              {getPageNumbers().map((p, i) =>
                p === '...' ? (
                  <span key={`ellipsis-${i}`} className="px-2 py-1.5 text-sm text-gray-400">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      p === page
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suiv.
              </button>
            </div>
          </div>
        );
      })()}

      {/* Main Modal with Tabs */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl m-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                {editingTenant ? `Modifier: ${editingTenant.name}` : 'Nouveau Tenant'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingTenant(null);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Tabs - only show for editing. Horizontally scrollable on small
                screens so all tabs remain reachable even with the new "Modules" tab. */}
            {editingTenant && (
              <div className="border-b border-gray-200">
                <div className="flex overflow-x-auto scrollbar-thin">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap shrink-0 ${
                        activeTab === tab.id
                          ? 'border-cyan-500 text-cyan-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <tab.icon className="h-4 w-4" />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <form onSubmit={editingTenant ? handleUpdate : handleCreate} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-4">
                {/* General Tab */}
                {(activeTab === 'general' || !editingTenant) && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">
                        Nom de l'organisation
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => {
                          const name = e.target.value;
                          // Auto-generate slug from name (only when creating)
                          const slug = !editingTenant
                            ? name
                                .toLowerCase()
                                .normalize('NFD')
                                .replace(/[\u0300-\u036f]/g, '') // Remove accents
                                .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
                                .replace(/\s+/g, '-') // Replace spaces with hyphens
                                .replace(/-+/g, '-') // Remove multiple hyphens
                                .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
                            : formData.slug;
                          setFormData({ ...formData, name, slug });
                        }}
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                        required
                      />
                    </div>

                    {!editingTenant && (
                      <>
                        <div>
                          <label className="block text-gray-700 text-sm font-medium mb-1">Slug (URL) - Auto-généré</label>
                          <input
                            type="text"
                            value={formData.slug}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                              })
                            }
                            className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                            required
                          />
                          <p className="text-xs text-gray-500 mt-1">Généré automatiquement, modifiable si besoin</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-gray-700 text-sm font-medium mb-1">Prénom Admin</label>
                            <input
                              type="text"
                              value={formData.firstName}
                              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-gray-700 text-sm font-medium mb-1">Nom Admin</label>
                            <input
                              type="text"
                              value={formData.lastName}
                              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-gray-700 text-sm font-medium mb-1">Email Admin</label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-gray-700 text-sm font-medium mb-1">Mot de passe</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={formData.password}
                              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                              className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 font-mono"
                              required
                              minLength={12}
                              placeholder="Minimum 12 caractères"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*';
                                let pwd = '';
                                for (let i = 0; i < 16; i++) {
                                  pwd += chars.charAt(Math.floor(Math.random() * chars.length));
                                }
                                setFormData({ ...formData, password: pwd });
                              }}
                              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg transition-colors"
                              title="Générer un mot de passe"
                            >
                              <Shuffle className="h-5 w-5 text-gray-600" />
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">Plan</label>
                      <select
                        value={formData.plan}
                        onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      >
                        <option value="FREE">Free</option>
                        <option value="BASIC">Basic</option>
                        <option value="PRO">Pro</option>
                        <option value="ENTERPRISE">Enterprise</option>
                      </select>
                    </div>

                    {editingTenant && (
                      <>
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div>
                            <label className="block text-gray-900 font-medium">Statut du contrat</label>
                            <p className="text-gray-500 text-sm">
                              {formData.isActive ? 'Le tenant est actif' : 'Le tenant est suspendu'}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              formData.isActive ? 'bg-primary-500' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                formData.isActive ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-gray-700 text-sm font-medium mb-1">Max Appareils (-1 = illimite)</label>
                            <input
                              type="number"
                              value={formData.maxDevices}
                              onChange={(e) =>
                                setFormData({ ...formData, maxDevices: parseInt(e.target.value) })
                              }
                              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                              min={-1}
                            />
                            {editingTenant?.subscriptions && editingTenant.subscriptions.filter(s => s.status === 'ACTIVE' || s.status === 'TRIALING').length > 0 && (
                              <p className="text-xs text-gray-500 mt-1">
                                Auto-calculé depuis les abonnements actifs ({editingTenant.subscriptions.filter(s => s.status === 'ACTIVE' || s.status === 'TRIALING').reduce((sum, s) => sum + (s.quantity || 0), 0)} véhicules)
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-gray-700 text-sm font-medium mb-1">Max Utilisateurs (-1 = illimite)</label>
                            <input
                              type="number"
                              value={formData.maxUsers}
                              onChange={(e) =>
                                setFormData({ ...formData, maxUsers: parseInt(e.target.value) })
                              }
                              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                              min={-1}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-gray-700 text-sm font-medium mb-1">Max Geofences (-1 = illimite)</label>
                            <input
                              type="number"
                              value={formData.maxGeofences}
                              onChange={(e) =>
                                setFormData({ ...formData, maxGeofences: parseInt(e.target.value) })
                              }
                              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                              min={-1}
                            />
                          </div>
                          <div>
                            <label className="block text-gray-700 text-sm font-medium mb-1">Retention (jours)</label>
                            <input
                              type="number"
                              value={formData.retentionDays}
                              onChange={(e) =>
                                setFormData({ ...formData, retentionDays: parseInt(e.target.value) })
                              }
                              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                              min={1}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Company Tab */}
                {activeTab === 'company' && editingTenant && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">Nom legal de l'entreprise</label>
                      <input
                        type="text"
                        value={formData.companyName}
                        onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">Adresse</label>
                      <input
                        type="text"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">Complement d'adresse</label>
                      <input
                        type="text"
                        value={formData.addressLine2}
                        onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">Code postal</label>
                        <input
                          type="text"
                          value={formData.postalCode}
                          onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                          className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">Ville</label>
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">Pays</label>
                        <input
                          type="text"
                          value={formData.country}
                          onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                          className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">Téléphone</label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">Email de contact</label>
                        <input
                          type="email"
                          value={formData.companyEmail}
                          onChange={(e) => setFormData({ ...formData, companyEmail: e.target.value })}
                          className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">Site web</label>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                        placeholder="https://"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">Numéro de TVA</label>
                        <input
                          type="text"
                          value={formData.vatNumber}
                          onChange={(e) => setFormData({ ...formData, vatNumber: e.target.value })}
                          className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                          placeholder="FR12345678901"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">SIRET</label>
                        <input
                          type="text"
                          value={formData.siret}
                          onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                          className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                          placeholder="12345678901234"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Billing Tab */}
                {activeTab === 'billing' && editingTenant && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">Email de facturation</label>
                      <input
                        type="email"
                        value={formData.billingEmail}
                        onChange={(e) => setFormData({ ...formData, billingEmail: e.target.value })}
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                        placeholder="facturation@entreprise.com"
                      />
                      <p className="text-gray-500 text-sm mt-1">
                        Adresse pour l'envoi des factures (si differente de l'email de contact)
                      </p>
                    </div>
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">Adresse de facturation</label>
                      <textarea
                        value={formData.billingAddress}
                        onChange={(e) => setFormData({ ...formData, billingAddress: e.target.value })}
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                        rows={3}
                        placeholder="Si differente de l'adresse principale..."
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">Type TVA</label>
                      <select
                        value={formData.vatRate}
                        onChange={(e) => setFormData({ ...formData, vatRate: Number(e.target.value) })}
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      >
                        <option value={17}>TVA 17%</option>
                        <option value={0}>Hors taxes (HT)</option>
                      </select>
                      <p className="text-gray-500 text-sm mt-1">
                        Taux de TVA appliqué aux factures de ce client
                      </p>
                    </div>
                  </div>
                )}

                {/* Subscriptions Tab */}
                {activeTab === 'subscriptions' && editingTenant && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-900">Abonnements</h3>
                      <button
                        type="button"
                        onClick={openNewSubscriptionModal}
                        className="btn-primary text-sm flex items-center gap-1"
                      >
                        <Plus className="h-4 w-4" />
                        Ajouter
                      </button>
                    </div>

                    {editingTenant.subscriptions && editingTenant.subscriptions.length > 0 ? (
                      <div className="space-y-4">
                        {editingTenant.subscriptions.map((sub) => (
                          <div
                            key={sub.id}
                            className="border border-gray-200 rounded-lg overflow-hidden"
                          >
                            {/* Subscription header - clickable for info */}
                            <div
                              className="p-4 hover:bg-gray-50 cursor-pointer"
                              onClick={() => { if (editingTenant) loadTenantDevices(editingTenant.id); setSelectedSubscription(sub); }}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-900">
                                      {sub.name || sub.plan?.displayName || 'Abonnement'}
                                    </span>
                                    <span
                                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        sub.status === 'ACTIVE'
                                          ? 'bg-green-100 text-green-700'
                                          : sub.status === 'TRIALING'
                                          ? 'bg-blue-100 text-blue-700'
                                          : sub.status === 'CANCELED'
                                          ? 'bg-red-100 text-red-700'
                                          : 'bg-gray-100 text-gray-700'
                                      }`}
                                    >
                                      {sub.status}
                                    </span>
                                    {isExpiringSoon(sub) && (
                                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700 animate-pulse">
                                        Expire bientôt
                                      </span>
                                    )}
                                  </div>
                                  <div className="mt-1 text-sm text-gray-500 space-y-1">
                                    <p>
                                      Plan: {sub.plan?.displayName} | {sub.quantity} trackers | {sub.pricingType === 'YEARLY' ? 'Annuel' : 'One-Shot'}
                                    </p>
                                    {(sub.currentPeriodEnd || sub.expiresAt) && (
                                      <p className={`flex items-center gap-1 ${
                                        new Date(sub.currentPeriodEnd || sub.expiresAt || '').getTime() < Date.now()
                                          ? 'text-red-500 font-medium'
                                          : isExpiringSoon(sub) ? 'text-orange-500 font-medium' : ''
                                      }`}>
                                        <Calendar className="h-3 w-3" />
                                        Expiration : {new Date(sub.currentPeriodEnd || sub.expiresAt || '').toLocaleDateString('fr-FR')}
                                      </p>
                                    )}
                                    <p className="flex items-center gap-2">
                                      {sub.autoRenewal ? (
                                        <span className="text-green-600 flex items-center gap-1">
                                          <RefreshCw className="h-3 w-3" /> Renouvellement auto
                                        </span>
                                      ) : (
                                        <span className="text-amber-600">Pas de renouvellement auto</span>
                                      )}
                                      {sub.alertsEnabled && (
                                        <span className="text-blue-600 flex items-center gap-1">
                                          <Bell className="h-3 w-3" /> Alertes actives
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                  {/* Bouton Payer via Stripe - si expire dans < 30j et prix Stripe configuré */}
                                  {isExpiringSoon(sub) && (sub.plan?.stripePriceIdYearly || sub.plan?.stripePriceIdOneShot) && (
                                    <button
                                      type="button"
                                      onClick={() => handlePaySubscription(sub)}
                                      disabled={payingSubId === sub.id}
                                      className="p-2 hover:bg-green-50 rounded-lg text-green-600 border border-green-200"
                                      title="Payer via Stripe"
                                    >
                                      {payingSubId === sub.id ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <CreditCard className="h-4 w-4" />
                                      )}
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => { if (editingTenant) loadTenantDevices(editingTenant.id); setSelectedSubscription(sub); }}
                                    className="p-2 hover:bg-gray-100 rounded-lg text-blue-600"
                                    title="Détails"
                                  >
                                    <Info className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRenewSubscription(sub.id)}
                                    className="p-2 hover:bg-gray-100 rounded-lg text-green-600"
                                    title="Renouveler"
                                  >
                                    <RefreshCw className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => openEditSubscriptionModal(sub)}
                                    className="p-2 hover:bg-gray-100 rounded-lg"
                                    title="Modifier"
                                  >
                                    <Pencil className="h-4 w-4 text-gray-500" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteSubscription(sub.id)}
                                    className="p-2 hover:bg-gray-100 rounded-lg"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="h-4 w-4 text-red-400" />
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Factures liées */}
                            {sub.invoices && sub.invoices.length > 0 && (
                              <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                                <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                                  <Receipt className="h-3 w-3" /> Factures ({sub.invoices.length})
                                </p>
                                <div className="space-y-1.5">
                                  {sub.invoices.slice(0, 5).map((inv) => (
                                    <div
                                      key={inv.id}
                                      className="flex items-center justify-between text-sm bg-white rounded px-3 py-2 border border-gray-100 hover:border-gray-300 cursor-pointer transition-colors"
                                      onClick={() => handleViewInvoicePdf(inv)}
                                    >
                                      <div className="flex items-center gap-2">
                                        <Eye className="h-3.5 w-3.5 text-gray-400" />
                                        <span className="text-gray-700 font-medium">
                                          {inv.invoiceNumber || 'Brouillon'}
                                        </span>
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getInvoiceStatusBadge(inv.status)}`}>
                                          {inv.status}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-gray-600">{formatAmount(inv.amountDue)}</span>
                                        {(inv.status === 'OPEN' || inv.status === 'DRAFT') && (
                                          <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); handleMarkInvoicePaid(inv); }}
                                            disabled={markingPaidId === inv.id}
                                            className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded border border-green-200 hover:bg-green-100"
                                            title="Marquer payée"
                                          >
                                            {markingPaidId === inv.id ? <Loader2 className="h-3 w-3 animate-spin inline" /> : <Check className="h-3 w-3 inline" />}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                  {sub.invoices.length > 5 && (
                                    <p className="text-xs text-gray-400 text-center mt-1">
                                      +{sub.invoices.length - 5} autres factures
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                        Aucun abonnement
                      </div>
                    )}

                    {/* Modules add-ons billing summary — what to bill on top of the
                        plan subscriptions. Loaded at the same time as this tab. */}
                    {(() => {
                      const billedModules = tenantModules.filter((r) => {
                        if (!r.state.enabled) return false;
                        if (r.definition.key === 'FIELD_SERVICE') {
                          return (r.state.seatsSold ?? 0) > 0;
                        }
                        return true;
                      });
                      const subsYearly = (editingTenant.subscriptions ?? [])
                        .filter((s) => s.status === 'ACTIVE' || s.status === 'TRIALING')
                        .reduce((sum, s) => {
                          const unit =
                            s.pricingType === 'YEARLY'
                              ? s.plan?.priceYearly ?? 0
                              : s.plan?.priceOneShot ?? 0;
                          return sum + unit * (s.quantity || 0);
                        }, 0);
                      const modulesYearlyCents = billedModules.reduce((sum, r) => {
                        if (r.definition.key === 'FIELD_SERVICE') {
                          return sum + (r.state.seatsSold ?? 0) * (r.state.pricePerSeatCents ?? 0);
                        }
                        const qty =
                          r.definition.key === 'EV' ? tenantEvVehicles : tenantActiveVehicles;
                        return sum + Math.max(0, qty) * r.state.pricePerVehicleCents;
                      }, 0);
                      const grandTotal = subsYearly + modulesYearlyCents / 100;

                      return (
                        <div className="rounded-lg border border-gray-200 overflow-hidden">
                          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                            <h4 className="font-medium text-gray-900 text-sm">
                              Modules complémentaires (à facturer en plus)
                            </h4>
                          </div>
                          {billedModules.length === 0 ? (
                            <p className="px-4 py-4 text-sm text-gray-500">
                              Aucun module activé pour ce tenant.
                            </p>
                          ) : (
                            <ul className="divide-y divide-gray-100">
                              {billedModules.map((r) => {
                                const isFs = r.definition.key === 'FIELD_SERVICE';
                                const qty = isFs
                                  ? r.state.seatsSold ?? 0
                                  : r.definition.key === 'EV'
                                    ? tenantEvVehicles
                                    : tenantActiveVehicles;
                                const unitCents = isFs
                                  ? r.state.pricePerSeatCents ?? 0
                                  : r.state.pricePerVehicleCents;
                                const lineCents = Math.max(0, qty) * unitCents;
                                return (
                                  <li
                                    key={r.definition.key}
                                    className="px-4 py-3 flex items-center justify-between text-sm"
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-gray-900">
                                        {r.definition.label}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {qty} × {(unitCents / 100).toFixed(2)} € /an{' '}
                                        {isFs ? '/ siège' : '/ véhicule'}
                                      </div>
                                    </div>
                                    <div className="text-sm font-semibold text-gray-900">
                                      {(lineCents / 100).toFixed(2)} € /an
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                            <span className="text-sm text-gray-600">
                              Total annuel (abonnements + modules)
                            </span>
                            <span className="text-base font-bold text-gray-900">
                              {grandTotal.toFixed(2)} € /an
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Modules Tab — Add-on catalog per tenant */}
                {activeTab === 'modules' && editingTenant && (
                  <div className="space-y-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <h3 className="font-medium text-gray-900 mb-1">
                          Modules additionnels
                        </h3>
                        <p className="text-sm text-gray-500 max-w-2xl">
                          Active des modules à la carte pour ce tenant. Chaque module est facturé mensuellement via Stripe au prix défini × nombre de véhicules actifs.
                          Essai gratuit de 14 jours disponible en 1 clic.
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full flex items-center gap-1.5">
                          <Cpu className="h-3.5 w-3.5" />
                          <strong>{tenantActiveVehicles}</strong> véhicule{tenantActiveVehicles > 1 ? 's' : ''} actif{tenantActiveVehicles > 1 ? 's' : ''}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setModuleEventsFilter('');
                            setModuleEventsOpen(true);
                            loadModuleEvents(editingTenant.id);
                          }}
                          className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full flex items-center gap-1.5"
                        >
                          <HistoryIcon className="h-3.5 w-3.5" />
                          Historique
                        </button>
                      </div>
                    </div>

                    {tenantModulesLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                      </div>
                    ) : tenantModules.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                        Aucun module au catalogue
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {[1, 2, 3].map((prio) => {
                          const rows = tenantModules.filter((m) => m.definition.priority === prio);
                          if (rows.length === 0) return null;
                          const prioLabel = prio === 1
                            ? '🟥 Priorité 1 — Gaps EcoMobility'
                            : prio === 2
                              ? '🟧 Priorité 2 — Forte valeur'
                              : '🟨 Priorité 3 — Différenciation avancée';
                          return (
                            <div key={prio}>
                              <h4 className="text-xs font-semibold uppercase text-gray-500 tracking-wide mb-2">{prioLabel}</h4>
                              <div className="space-y-2">
                                {rows.map((row) => {
                                  const now = Date.now();
                                  const trialActive = row.state.trialEndsAt && new Date(row.state.trialEndsAt).getTime() > now;
                                  const isActive = row.state.enabled || trialActive;
                                  const priceEuros = (row.state.pricePerVehicleCents / 100).toFixed(2);
                                  // EV bills per opted-in vehicle (devices.evEnabled), not all active vehicles.
                                  const billableQty = row.definition.key === 'EV' ? tenantEvVehicles : tenantActiveVehicles;
                                  const monthlyTotal = ((row.state.pricePerVehicleCents * Math.max(1, billableQty)) / 100).toFixed(2);
                                  const busy = moduleBusyKey === row.definition.key;
                                  return (
                                    <div
                                      key={row.definition.key}
                                      className={`border rounded-lg p-4 transition-colors ${
                                        isActive
                                          ? 'border-primary-300 bg-primary-50/30'
                                          : 'border-gray-200 bg-white'
                                      }`}
                                    >
                                      <div className="flex items-start justify-between gap-4 flex-wrap">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 flex-wrap mb-1">
                                            <h5 className="font-semibold text-gray-900">{row.definition.label}</h5>
                                            {row.state.enabled && (
                                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                                                <Zap className="h-3 w-3" /> ACTIF
                                              </span>
                                            )}
                                            {trialActive && !row.state.enabled && (
                                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                                ESSAI ({Math.ceil((new Date(row.state.trialEndsAt!).getTime() - now) / 86400000)} j restants)
                                              </span>
                                            )}
                                            {!isActive && row.state.deactivatedAt && (
                                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                                Désactivé
                                              </span>
                                            )}
                                            {row.definition.dependencies && row.definition.dependencies.length > 0 && (
                                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700" title={`Dépend de : ${row.definition.dependencies.join(', ')}`}>
                                                Dépend de {row.definition.dependencies.join(', ')}
                                              </span>
                                            )}
                                          </div>
                                          <p className="text-xs text-gray-600 mb-3">{row.definition.description}</p>
                                          <div className="flex items-center gap-3 flex-wrap text-xs">
                                            {row.definition.key !== 'FIELD_SERVICE' && (
                                              <>
                                                <label className="flex items-center gap-1.5 text-gray-700">
                                                  Prix/véh/an :
                                                  <input
                                                    type="number"
                                                    min={0}
                                                    step={0.5}
                                                    value={priceEuros}
                                                    onChange={(e) => {
                                                      const v = parseFloat(e.target.value);
                                                      if (!isNaN(v)) {
                                                        const cents = Math.round(v * 100);
                                                        setTenantModules((prev) => prev.map((m) => m.definition.key === row.definition.key ? { ...m, state: { ...m.state, pricePerVehicleCents: cents } } : m));
                                                      }
                                                    }}
                                                    onBlur={(e) => {
                                                      const v = parseFloat(e.target.value);
                                                      if (!isNaN(v)) {
                                                        const cents = Math.round(v * 100);
                                                        if (cents !== (tenantModules.find((m) => m.definition.key === row.definition.key)?.state.pricePerVehicleCents ?? 0)) {
                                                          // already updated optimistically — persist
                                                          handleUpdateModulePrice(row.definition.key, cents);
                                                        }
                                                      }
                                                    }}
                                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-right"
                                                  />
                                                  <span className="text-gray-500">€</span>
                                                </label>
                                                <span className="text-gray-400">·</span>
                                                <span className="text-gray-700">
                                                  Total estimé : <strong>{monthlyTotal} €/an</strong>
                                                </span>
                                                <span className="text-gray-400">·</span>
                                                <span className="text-gray-500">
                                                  Défaut catalogue : {(row.definition.defaultPriceCents / 100).toFixed(2)} €
                                                </span>
                                              </>
                                            )}
                                            {row.definition.key === 'EV' && (
                                              <>
                                                <span className="text-gray-400">·</span>
                                                <span className="text-gray-700">
                                                  Véhicules EV activés : <strong>{tenantEvVehicles}</strong>
                                                </span>
                                              </>
                                            )}
                                            {row.state.stripeSubscriptionItemId && (
                                              <span className="text-green-700 text-[10px] font-mono">Stripe: {row.state.stripeSubscriptionItemId.slice(0, 14)}…</span>
                                            )}
                                          </div>
                                          {row.definition.key === 'FIELD_SERVICE' && (
                                            <div className="mt-3 pt-3 border-t border-gray-200 bg-white/60 -mx-4 px-4 pb-1">
                                              <div className="text-[10px] font-semibold uppercase text-gray-500 tracking-wide mb-2">
                                                Facturation sièges Mission Driver (annuel, payé/perdu)
                                              </div>
                                              <div className="flex items-end gap-3 flex-wrap text-xs">
                                                <label className="flex flex-col gap-1">
                                                  <span className="text-gray-600">Sièges vendus</span>
                                                  <input
                                                    type="number"
                                                    min={0}
                                                    step={1}
                                                    defaultValue={row.state.seatsSold ?? 0}
                                                    onBlur={(e) => {
                                                      const v = parseInt(e.target.value, 10);
                                                      if (isNaN(v) || v < 0) return;
                                                      const currentPrice =
                                                        row.state.pricePerSeatCents ?? 0;
                                                      if (v !== (row.state.seatsSold ?? 0)) {
                                                        handleUpdateModuleSeats(
                                                          row.definition.key,
                                                          v,
                                                          currentPrice,
                                                        );
                                                      }
                                                    }}
                                                    className="w-24 px-2 py-1 border border-gray-300 rounded text-right"
                                                  />
                                                </label>
                                                <label className="flex flex-col gap-1">
                                                  <span className="text-gray-600">Prix annuel / siège</span>
                                                  <div className="flex items-center gap-1">
                                                    <input
                                                      type="number"
                                                      min={0}
                                                      step={1}
                                                      defaultValue={((row.state.pricePerSeatCents ?? 0) / 100).toFixed(2)}
                                                      onBlur={(e) => {
                                                        const v = parseFloat(e.target.value);
                                                        if (isNaN(v) || v < 0) return;
                                                        const cents = Math.round(v * 100);
                                                        const currentSeats = row.state.seatsSold ?? 0;
                                                        if (cents !== (row.state.pricePerSeatCents ?? 0)) {
                                                          handleUpdateModuleSeats(
                                                            row.definition.key,
                                                            currentSeats,
                                                            cents,
                                                          );
                                                        }
                                                      }}
                                                      className="w-24 px-2 py-1 border border-gray-300 rounded text-right"
                                                    />
                                                    <span className="text-gray-500">€</span>
                                                  </div>
                                                </label>
                                                <div className="flex flex-col gap-1">
                                                  <span className="text-gray-600">Sièges utilisés</span>
                                                  <div
                                                    className={`px-2 py-1 rounded text-center font-medium ${
                                                      (row.state.missionSeatsUsed ?? 0) >
                                                      (row.state.seatsSold ?? 0)
                                                        ? 'bg-red-100 text-red-700'
                                                        : (row.state.missionSeatsUsed ?? 0) ===
                                                            (row.state.seatsSold ?? 0) &&
                                                          (row.state.seatsSold ?? 0) > 0
                                                          ? 'bg-amber-100 text-amber-700'
                                                          : 'bg-gray-100 text-gray-700'
                                                    }`}
                                                  >
                                                    {row.state.missionSeatsUsed ?? 0} /{' '}
                                                    {row.state.seatsSold ?? 0}
                                                  </div>
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                  <span className="text-gray-600">Total annuel</span>
                                                  <div className="px-2 py-1 rounded bg-gray-100 text-gray-900 font-semibold min-w-[80px] text-right">
                                                    {(
                                                      ((row.state.seatsSold ?? 0) *
                                                        (row.state.pricePerSeatCents ?? 0)) /
                                                      100
                                                    ).toFixed(2)}{' '}
                                                    €
                                                  </div>
                                                </div>
                                              </div>
                                              <p className="text-[10px] text-gray-500 mt-2">
                                                Les sièges annuels sont fermes : pas de remboursement ni proration. Un ajout en cours d'année est facturé au prix annuel plein, aligné sur la date de renouvellement. La répartition nominative se fait sur la page Conducteurs.
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                        <div className="flex flex-col gap-1.5 shrink-0">
                                          {!isActive ? (
                                            <>
                                              <button
                                                type="button"
                                                onClick={() => handleActivateModule(row.definition.key, false, row.state.pricePerVehicleCents)}
                                                disabled={busy}
                                                className="text-xs px-3 py-1.5 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 text-white rounded flex items-center gap-1"
                                              >
                                                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
                                                Activer
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => handleActivateModule(row.definition.key, true, row.state.pricePerVehicleCents)}
                                                disabled={busy}
                                                className="text-xs px-3 py-1.5 bg-amber-100 hover:bg-amber-200 disabled:bg-gray-200 text-amber-800 rounded flex items-center gap-1"
                                                title="Essai gratuit de 14 jours"
                                              >
                                                <Calendar className="h-3 w-3" />
                                                Essai 14 j
                                              </button>
                                            </>
                                          ) : (
                                            <button
                                              type="button"
                                              onClick={() => handleDeactivateModule(row.definition.key)}
                                              disabled={busy}
                                              className="text-xs px-3 py-1.5 bg-red-50 hover:bg-red-100 disabled:bg-gray-200 text-red-700 rounded flex items-center gap-1"
                                            >
                                              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />}
                                              Désactiver
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Module events drawer */}
                    {moduleEventsOpen && (
                      <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setModuleEventsOpen(false)}>
                        <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-between p-4 border-b border-gray-200">
                            <h3 className="font-semibold flex items-center gap-2">
                              <HistoryIcon className="h-4 w-4" /> Historique des modules
                            </h3>
                            <button onClick={() => setModuleEventsOpen(false)} className="text-gray-400 hover:text-gray-600">
                              <X className="h-5 w-5" />
                            </button>
                          </div>
                          <div className="p-4 border-b border-gray-100">
                            <select
                              value={moduleEventsFilter}
                              onChange={(e) => {
                                setModuleEventsFilter(e.target.value);
                                if (editingTenant) loadModuleEvents(editingTenant.id, e.target.value || undefined);
                              }}
                              className="px-3 py-1.5 border border-gray-300 rounded text-sm"
                            >
                              <option value="">Tous les modules</option>
                              {tenantModules.map((m) => (
                                <option key={m.definition.key} value={m.definition.key}>
                                  {m.definition.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex-1 overflow-y-auto p-4">
                            {moduleEvents.length === 0 ? (
                              <p className="text-center text-gray-500 py-8">Aucun événement</p>
                            ) : (
                              <div className="space-y-2">
                                {moduleEvents.map((ev) => (
                                  <div key={ev.id} className="border border-gray-200 rounded p-3 text-xs">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                      <span className="font-semibold text-gray-900">{ev.moduleKey}</span>
                                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                        ev.action === 'ACTIVATED' ? 'bg-green-100 text-green-700'
                                        : ev.action === 'DEACTIVATED' ? 'bg-red-100 text-red-700'
                                        : ev.action === 'TRIAL_STARTED' ? 'bg-amber-100 text-amber-700'
                                        : ev.action === 'TRIAL_EXPIRED' ? 'bg-gray-100 text-gray-600'
                                        : ev.action === 'PRICE_CHANGED' ? 'bg-blue-100 text-blue-700'
                                        : 'bg-purple-100 text-purple-700'
                                      }`}>{ev.action}</span>
                                      <span className="text-gray-400">{new Date(ev.createdAt).toLocaleString('fr-FR')}</span>
                                      {ev.triggeredBy && (
                                        <span className="text-gray-500">par {ev.triggeredBy.firstName} {ev.triggeredBy.lastName}</span>
                                      )}
                                    </div>
                                    {ev.reason && <p className="text-gray-600">{ev.reason}</p>}
                                    {(ev.previousState != null || ev.newState != null) && (
                                      <details className="mt-1">
                                        <summary className="cursor-pointer text-gray-500">détails</summary>
                                        <pre className="mt-1 p-2 bg-gray-50 rounded text-[10px] overflow-x-auto">{JSON.stringify({ prev: ev.previousState, next: ev.newState }, null, 2)}</pre>
                                      </details>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Security Tab - Duo Configuration + API Keys */}
                {activeTab === 'security' && editingTenant && (
                  <div className="space-y-6">
                    {/* API Keys Permission */}
                    <div className="space-y-3">
                      <div>
                        <h3 className="font-medium text-gray-900 mb-1">Accès API</h3>
                        <p className="text-sm text-gray-500">
                          Autorisez ou bloquez l'utilisation des clés API pour ce tenant. Par défaut, l'accès API est désactivé.
                        </p>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div>
                          <p className="font-medium text-gray-900">Activer les clés API</p>
                          <p className="text-xs text-gray-500">Permet au tenant de créer et utiliser des clés d'accès à l'API publique</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, apiKeysEnabled: !formData.apiKeysEnabled })}
                          className={`relative inline-flex h-6 w-11 min-w-[44px] flex-shrink-0 items-center rounded-full transition-colors ${
                            formData.apiKeysEnabled ? 'bg-primary-500' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.apiKeysEnabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    <hr className="border-gray-200" />

                    {/* Duo Security Section */}
                    <div className="space-y-4">
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">Duo Security 2FA</h3>
                      <p className="text-sm text-gray-500">
                        Configurez Duo Security pour activer l'authentification a deux facteurs via Duo pour les utilisateurs de ce tenant.
                      </p>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div>
                        <p className="font-medium text-gray-900">Activer Duo 2FA</p>
                        <p className="text-xs text-gray-500">Permet aux utilisateurs de choisir Duo comme methode 2FA</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, duoEnabled: !formData.duoEnabled })}
                        className={`relative inline-flex h-6 w-11 min-w-[44px] flex-shrink-0 items-center rounded-full transition-colors ${
                          formData.duoEnabled ? 'bg-primary-500' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            formData.duoEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">Client ID</label>
                      <input
                        type="text"
                        value={formData.duoClientId}
                        onChange={(e) => setFormData({ ...formData, duoClientId: e.target.value })}
                        placeholder="DIXXXXXXXXXXXXXXXXXX"
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">Client Secret</label>
                      <input
                        type="password"
                        value={formData.duoClientSecret}
                        onChange={(e) => setFormData({ ...formData, duoClientSecret: e.target.value })}
                        placeholder="••••••••••••••••"
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">API Hostname</label>
                      <input
                        type="text"
                        value={formData.duoApiHostname}
                        onChange={(e) => setFormData({ ...formData, duoApiHostname: e.target.value })}
                        placeholder="api-XXXXXXXX.duosecurity.com"
                        className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      />
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleDuoHealthCheck}
                        disabled={duoHealthCheckLoading || !formData.duoClientId || !formData.duoClientSecret || !formData.duoApiHostname}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg disabled:opacity-50 transition-colors text-sm"
                      >
                        {duoHealthCheckLoading ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Shield className="h-4 w-4 mr-2" />
                        )}
                        Tester la connexion Duo
                      </button>
                    </div>

                    {duoHealthCheckResult && (
                      <div className={`p-4 rounded-lg border ${
                        duoHealthCheckResult.success
                          ? 'bg-green-50 border-green-200 text-green-700'
                          : 'bg-red-50 border-red-200 text-red-700'
                      }`}>
                        <div className="flex items-center gap-2">
                          {duoHealthCheckResult.success ? (
                            <Check className="h-4 w-4 flex-shrink-0" />
                          ) : (
                            <X className="h-4 w-4 flex-shrink-0" />
                          )}
                          <span className="text-sm">{duoHealthCheckResult.message}</span>
                        </div>
                      </div>
                    )}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingTenant(null);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                {activeTab !== 'subscriptions' && (
                  <button type="submit" className="btn-primary">
                    {editingTenant ? 'Enregistrer' : 'Créer'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg m-4">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingSubscription ? 'Modifier l\'abonnement' : 'Nouvel abonnement'}
              </h2>
            </div>
            <form onSubmit={editingSubscription ? handleUpdateSubscription : handleCreateSubscription}>
              <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">Nom (optionnel)</label>
                  <input
                    type="text"
                    value={subscriptionForm.name}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, name: e.target.value })}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    placeholder="Ex: Flotte véhicules légers"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">Plan</label>
                  <select
                    value={subscriptionForm.planId}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, planId: e.target.value })}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    required
                  >
                    <option value="">Sélectionner un plan</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.displayName} - {plan.priceYearly}EUR/an/tracker
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">Quantite (trackers)</label>
                    <input
                      type="number"
                      value={subscriptionForm.quantity}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, quantity: parseInt(e.target.value) || 1 })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                      min={1}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">Type de tarification</label>
                    <select
                      value={subscriptionForm.pricingType}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, pricingType: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    >
                      <option value="YEARLY">Annuel</option>
                      <option value="ONESHOT">Achat unique</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">Date de debut</label>
                    <input
                      type="date"
                      value={subscriptionForm.startDate}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, startDate: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">Date d'expiration</label>
                    <input
                      type="date"
                      value={subscriptionForm.expiresAt}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, expiresAt: e.target.value })}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">Statut</label>
                  <select
                    value={subscriptionForm.status}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, status: e.target.value })}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                  >
                    <option value="ACTIVE">Actif</option>
                    <option value="TRIALING">Essai</option>
                    <option value="PAST_DUE">En retard</option>
                    <option value="CANCELED">Annule</option>
                    <option value="UNPAID">Non paye</option>
                  </select>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <label className="text-gray-900 font-medium">Renouvellement automatique</label>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSubscriptionForm({ ...subscriptionForm, autoRenewal: !subscriptionForm.autoRenewal })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      subscriptionForm.autoRenewal ? 'bg-primary-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        subscriptionForm.autoRenewal ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <label className="text-gray-900 font-medium">Alertes d'expiration</label>
                    <p className="text-gray-500 text-sm">Alertes a {subscriptionForm.alertDays.join(', ')} jours</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSubscriptionForm({ ...subscriptionForm, alertsEnabled: !subscriptionForm.alertsEnabled })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      subscriptionForm.alertsEnabled ? 'bg-primary-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        subscriptionForm.alertsEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                {/* Multi-select trackers */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    Trackers liés ({selectedDeviceIds.length})
                  </label>
                  <div className="relative" ref={subDeviceDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setShowDeviceDropdown(!showDeviceDropdown)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-left focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 flex items-center justify-between"
                    >
                      {selectedDeviceIds.length === 0
                        ? <span className="text-gray-400">Aucun tracker sélectionné</span>
                        : <span>{selectedDeviceIds.length} tracker{selectedDeviceIds.length > 1 ? 's' : ''} sélectionné{selectedDeviceIds.length > 1 ? 's' : ''}</span>
                      }
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </button>
                    {showDeviceDropdown && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg">
                        <div className="p-2 border-b border-gray-100">
                          <input
                            type="text"
                            value={deviceSearch}
                            onChange={(e) => setDeviceSearch(e.target.value)}
                            placeholder="Rechercher par nom, plaque, IMEI..."
                            className="w-full px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {tenantDevices.length === 0 ? (
                            <div className="px-4 py-2 text-sm text-gray-400">Aucun tracker pour ce tenant</div>
                          ) : filteredDevices.length === 0 ? (
                            <div className="px-4 py-2 text-sm text-gray-400">Aucun résultat</div>
                          ) : (
                            filteredDevices.map((device) => {
                              const isSelected = selectedDeviceIds.includes(device.id);
                              return (
                                <button
                                  key={device.id}
                                  type="button"
                                  onClick={() => {
                                    setSelectedDeviceIds((prev) =>
                                      isSelected
                                        ? prev.filter((id) => id !== device.id)
                                        : [...prev, device.id]
                                    );
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-primary-50 flex items-center gap-2 transition-colors"
                                >
                                  <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${isSelected ? 'bg-primary-500 border-primary-500' : 'border-gray-300'}`}>
                                    {isSelected && <Check className="h-3 w-3 text-white" />}
                                  </div>
                                  <span className="text-gray-900 truncate">{device.name}</span>
                                  {device.plate && (
                                    <span className="text-gray-400 text-xs flex-shrink-0">{device.plate}</span>
                                  )}
                                  <span className="text-gray-600 text-sm font-mono ml-auto flex-shrink-0">{device.imei}</span>
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {selectedDeviceIds.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {selectedDeviceIds.map((did) => {
                        const device = tenantDevices.find((d) => d.id === did);
                        return (
                          <span
                            key={did}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-700 rounded-full text-xs"
                          >
                            {device ? (device.plate || device.name) : did.slice(0, 8)}
                            <button
                              type="button"
                              onClick={() => setSelectedDeviceIds((prev) => prev.filter((id) => id !== did))}
                              className="hover:text-red-500"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">Notes internes</label>
                  <textarea
                    value={subscriptionForm.notes}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, notes: e.target.value })}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    rows={3}
                    placeholder="Notes visibles uniquement par les administrateurs..."
                  />
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowSubscriptionModal(false)}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingSubscription ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && passwordTenant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Reinitialiser le mot de passe
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                Tenant: {passwordTenant.name}
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Nouveau mot de passe
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setPasswordError('');
                        setPasswordCopied(false);
                      }}
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 font-mono"
                      placeholder="Minimum 8 caracteres"
                      minLength={8}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg transition-colors"
                    title="Générer un mot de passe"
                  >
                    <Shuffle className="h-5 w-5 text-gray-600" />
                  </button>
                  <button
                    type="button"
                    onClick={copyPassword}
                    disabled={!newPassword}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg transition-colors disabled:opacity-50"
                    title="Copier le mot de passe"
                  >
                    {passwordCopied ? (
                      <Check className="h-5 w-5 text-green-600" />
                    ) : (
                      <Copy className="h-5 w-5 text-gray-600" />
                    )}
                  </button>
                </div>
                {passwordError && (
                  <p className="text-red-500 text-sm mt-1">{passwordError}</p>
                )}
                {passwordCopied && (
                  <p className="text-green-600 text-sm mt-1">Mot de passe copie !</p>
                )}
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordTenant(null);
                  setNewPassword('');
                  setPasswordError('');
                }}
                className="btn-secondary"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleResetPassword}
                className="btn-primary"
              >
                Reinitialiser
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal Info Abonnement */}
      {selectedSubscription && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg m-4 max-h-[85vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Détails de l'abonnement</h2>
              <button onClick={() => setSelectedSubscription(null)} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto space-y-4">
              {/* Header */}
              <div className="flex items-center gap-3">
                <span className="text-xl font-semibold text-gray-900">
                  {selectedSubscription.name || selectedSubscription.plan?.displayName || 'Abonnement'}
                </span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  selectedSubscription.status === 'ACTIVE' ? 'bg-green-100 text-green-700'
                    : selectedSubscription.status === 'TRIALING' ? 'bg-blue-100 text-blue-700'
                    : selectedSubscription.status === 'CANCELED' ? 'bg-red-100 text-red-700'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {selectedSubscription.status}
                </span>
              </div>

              {/* Détails */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500 text-xs mb-1">Plan</p>
                  <p className="font-medium text-gray-900">{selectedSubscription.plan?.displayName || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500 text-xs mb-1">Type de pricing</p>
                  <p className="font-medium text-gray-900">{selectedSubscription.pricingType === 'YEARLY' ? 'Annuel' : 'One-Shot'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500 text-xs mb-1">Quantité trackers</p>
                  <p className="font-medium text-gray-900">{selectedSubscription.quantity}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500 text-xs mb-1">Renouvellement auto</p>
                  <p className={`font-medium ${selectedSubscription.autoRenewal ? 'text-green-700' : 'text-amber-700'}`}>
                    {selectedSubscription.autoRenewal ? 'Oui' : 'Non'}
                  </p>
                </div>
                {selectedSubscription.startDate && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-500 text-xs mb-1">Date de début</p>
                    <p className="font-medium text-gray-900">{new Date(selectedSubscription.startDate).toLocaleDateString('fr-FR')}</p>
                  </div>
                )}
                {(selectedSubscription.currentPeriodEnd || selectedSubscription.expiresAt) && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-500 text-xs mb-1">Date d'expiration</p>
                    <p className={`font-medium ${
                      new Date(selectedSubscription.currentPeriodEnd || selectedSubscription.expiresAt || '').getTime() < Date.now()
                        ? 'text-red-600'
                        : isExpiringSoon(selectedSubscription) ? 'text-orange-600' : 'text-gray-900'
                    }`}>
                      {new Date(selectedSubscription.currentPeriodEnd || selectedSubscription.expiresAt || '').toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                )}
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500 text-xs mb-1">Alertes</p>
                  <p className="font-medium text-gray-900">
                    {selectedSubscription.alertsEnabled
                      ? `Activées (${selectedSubscription.alertDays?.join(', ')} jours)`
                      : 'Désactivées'}
                  </p>
                </div>
              </div>

              {/* Notes */}
              {selectedSubscription.notes && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500 text-xs mb-1">Notes</p>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedSubscription.notes}</p>
                </div>
              )}

              {/* Trackers liés */}
              {selectedSubscription.deviceIds && selectedSubscription.deviceIds.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    <Cpu className="h-4 w-4" /> Trackers liés ({selectedSubscription.deviceIds.length})
                  </p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {selectedSubscription.deviceIds.map((deviceId) => {
                      const device = tenantDevices.find((d) => d.id === deviceId);
                      return (
                        <div key={deviceId} className="flex items-center gap-2 text-sm bg-gray-50 rounded px-3 py-1.5">
                          <Cpu className="h-3.5 w-3.5 text-gray-400" />
                          <span className="text-gray-900">{device?.name || deviceId}</span>
                          {device?.plate && <span className="text-gray-500">({device.plate})</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Factures */}
              {selectedSubscription.invoices && selectedSubscription.invoices.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    <Receipt className="h-4 w-4" /> Factures ({selectedSubscription.invoices.length})
                  </p>
                  <div className="space-y-1.5">
                    {selectedSubscription.invoices.map((inv) => (
                      <div
                        key={inv.id}
                        className="flex items-center justify-between text-sm bg-gray-50 rounded px-3 py-2 hover:bg-gray-100 cursor-pointer transition-colors"
                        onClick={() => { setSelectedSubscription(null); handleViewInvoicePdf(inv); }}
                      >
                        <div className="flex items-center gap-2">
                          <Eye className="h-3.5 w-3.5 text-gray-400" />
                          <span className="font-medium text-gray-700">{inv.invoiceNumber || 'Brouillon'}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getInvoiceStatusBadge(inv.status)}`}>
                            {inv.status}
                          </span>
                        </div>
                        <span className="text-gray-600">{formatAmount(inv.amountDue)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              {isExpiringSoon(selectedSubscription) && (selectedSubscription.plan?.stripePriceIdYearly || selectedSubscription.plan?.stripePriceIdOneShot) && (
                <button
                  type="button"
                  onClick={() => handlePaySubscription(selectedSubscription)}
                  disabled={payingSubId === selectedSubscription.id}
                  className="btn-primary flex items-center gap-2 text-sm"
                >
                  {payingSubId === selectedSubscription.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                  Payer via Stripe
                </button>
              )}
              <button type="button" onClick={() => setSelectedSubscription(null)} className="btn-secondary text-sm">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal PDF Viewer */}
      {viewingInvoice && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[80]">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl m-4 h-[90vh] flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-gray-600" />
                <div>
                  <h2 className="font-semibold text-gray-900">
                    {viewingInvoice.invoiceNumber || 'Brouillon'}
                  </h2>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>{formatAmount(viewingInvoice.amountDue)}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getInvoiceStatusBadge(viewingInvoice.status)}`}>
                      {viewingInvoice.status}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {pdfUrl && (
                  <a
                    href={pdfUrl}
                    download={`${viewingInvoice.invoiceNumber || 'facture'}.pdf`}
                    className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
                    title="Télécharger"
                  >
                    <Download className="h-5 w-5" />
                  </a>
                )}
                {(viewingInvoice.status === 'OPEN' || viewingInvoice.status === 'DRAFT') && (
                  <button
                    type="button"
                    onClick={() => handleMarkInvoicePaid(viewingInvoice)}
                    disabled={markingPaidId === viewingInvoice.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg border border-green-200 hover:bg-green-100 text-sm"
                  >
                    {markingPaidId === viewingInvoice.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    Marquer payée
                  </button>
                )}
                <button onClick={closePdfViewer} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
            {/* PDF iframe */}
            <div className="flex-1 bg-gray-100">
              {loadingPdf ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : pdfUrl ? (
                <iframe src={pdfUrl} className="w-full h-full border-0" title="Facture PDF" />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  Erreur de chargement du PDF
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
